'use client'
import { motion, useInView } from 'framer-motion'
import { useMemo, useRef, useEffect, useState, useCallback } from 'react'
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import type { PageUrlMap } from "@/types/blog";
import {
    buildSpecialUrl,
    buildCategoryUrl,
    resolveProductTypeKeyForCategoryUrl,
} from "@/lib/url-helpers";
import { resolveUrlFromMap } from "@/lib/blog-transformers";
import { shouldEmbedBlockPropsInDom } from "@/lib/embed-block-props-dom";

interface CategoryData {
    id: number;
    name: string;
    slug?: string;
    description?: string;
    cover_image?: string;
    type?: string;
}

const SPECIAL_TOUR_SECTION_SHELL =
    'mx-auto w-full min-w-0 max-w-full px-4 font-sans sm:px-6 lg:w-8/12 lg:px-0 xl:w-9/12';
const SPECIAL_TOUR_SECTION_MY =
    'my-16 sm:my-24 md:my-32 lg:my-[160px]';

interface SpecialTourGridProps {
    title?: string;
    categoryIds?: number[];
    languageId?: number;
    language?: string;
    languageCode?: string;
    categories?: CategoryData[];
    enableClientFetch?: boolean;
    pageUrls?: PageUrlMap;
    defaultLanguage?: string;
    /** TourCatGrid ile aynı: private-tours / group-tours / tour / auto */
    categoryUrlProductType?: string;
    __pageContext?: {
        languageId?: number;
        language?: string;
        languageCode?: string;
        pageUrls?: PageUrlMap;
        categoryUrlProductType?: string;
    };
}

/** Tasarıma uygun iskelet: başlık + View All + 3 sütun grid (7 kart, sonuncu ortada). */
function SpecialTourGridSkeleton() {
    const pulse = 'animate-pulse rounded-lg bg-gray-200/90 dark:bg-gray-700/80';
    return (
        <section className={`${SPECIAL_TOUR_SECTION_SHELL} ${SPECIAL_TOUR_SECTION_MY}`} aria-busy="true" aria-label="Yükleniyor">
            <div className="flex flex-row justify-between items-start gap-4">
                <div className="flex flex-col gap-3 min-w-0 flex-1">
                    <div className={`h-10 w-64 max-w-[80%] ${pulse}`} />
                    <div className={`h-1.5 w-[100px] rounded-sm ${pulse}`} />
                </div>
                <div className={`h-12 w-28 shrink-0 rounded-sm ${pulse}`} />
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:mt-12 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
                {Array.from({ length: 7 }).map((_, i) => (
                    <div
                        key={i}
                        className={`h-32 w-full ${pulse} ${i === 6 ? 'md:col-start-2 lg:col-start-2' : ''}`}
                    />
                ))}
            </div>
        </section>
    );
}

export default function SpecialTourGrid({ 
    title: propTitle = 'Turkey Special Tours',
    categoryIds: propCategoryIds,
    languageId: propLanguageId,
    language: propLanguage,
    languageCode: propLanguageCode,
    categories: initialCategories,
    enableClientFetch = false,
    pageUrls: propPageUrls,
    defaultLanguage: propDefaultLanguage = 'en',
    categoryUrlProductType: propCategoryUrlProductType,
    __pageContext
}: SpecialTourGridProps) {
    const pathname = usePathname();
    const titleRef = useRef(null);
    const isTitleInView = useInView(titleRef, { once: true, margin: "-100px" });

    const languageId = propLanguageId || __pageContext?.languageId;
    const resolvedLanguageCode = propLanguage || propLanguageCode || __pageContext?.language || __pageContext?.languageCode;
    const pageUrls = propPageUrls || __pageContext?.pageUrls;
    const defaultLanguage = propDefaultLanguage;
    const categoryUrlProductType =
        propCategoryUrlProductType ?? __pageContext?.categoryUrlProductType;

    const [categories, setCategories] = useState<CategoryData[]>(
        () => (Array.isArray(initialCategories) ? initialCategories : [])
    );
    const [loading, setLoading] = useState(() => !Array.isArray(initialCategories));
    const [error, setError] = useState<string | null>(null);
    const [componentProps, setComponentProps] = useState<{ categoryIds?: number[]; title?: string }>({});

    const { getVariable } = useLanguageVariables();

    useEffect(() => {
        if (!shouldEmbedBlockPropsInDom()) return;
        const updateProps = () => {
            const element = document.querySelector('[data-component-path="sections/SpecialTourGrid"]');
            if (element) {
                const propsAttr = element.getAttribute('data-component-props');
                if (propsAttr) {
                    try {
                        const props = JSON.parse(propsAttr);
                        setComponentProps(props);
                    } catch {
                        // Props parse hatası
                    }
                }
            }
        };

        updateProps();

        const observer = new MutationObserver(updateProps);
        const element = document.querySelector('[data-component-path="sections/SpecialTourGrid"]');
        if (element) {
            observer.observe(element, {
                attributes: true,
                attributeFilter: ['data-component-props']
            });
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    const categoryIds = propCategoryIds || componentProps.categoryIds || undefined;
    const title = propTitle || componentProps.title || 'Turkey Special Tours';

    useEffect(() => {
        if (Array.isArray(initialCategories)) {
            setCategories(initialCategories);
            setLoading(false);
            return;
        }

        if (!enableClientFetch) {
            setLoading(false);
            setCategories([]);
            return;
        }

        async function fetchCategories() {
            if (!categoryIds || categoryIds.length === 0) {
                setLoading(false);
                setCategories([]);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const params = new URLSearchParams();
                
                if (languageId) {
                    params.set('language_id', String(languageId));
                }

                if (categoryIds && categoryIds.length > 0) {
                    categoryIds.forEach(id => params.append('category_ids[]', String(id)));
                }

                const response = await fetch(`/api/product-categories?${params.toString()}`);
                
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const errorMessage = errorData.message || errorData.error || `API error: ${response.status}`;
                    throw new Error(errorMessage);
                }

                const data = await response.json();
                const categoriesData = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
                setCategories(categoriesData);
            } catch (err) {
                console.error('Kategoriler yüklenirken hata:', err);
                const errorMessage = err instanceof Error ? err.message : 'Kategoriler yüklenemedi';
                setError(errorMessage);
                setCategories([]);
            } finally {
                setLoading(false);
            }
        }

        fetchCategories();
    }, [languageId, categoryIds, enableClientFetch, initialCategories]);

    const languagePrefix = useMemo(() => {
        if (!pathname) {
            return '';
        }

        const segments = pathname.split('/').filter(Boolean);
        const maybeLang = segments[0];

        if (maybeLang && /^[a-z]{2}$/i.test(maybeLang)) {
            return `/${maybeLang.toLowerCase()}`;
        }

        return '';
    }, [pathname]);
    
    const productTypeForSpecial = useMemo(() => {
        if (categories && categories.length > 0 && categories[0].type) {
            return resolveProductTypeKeyForCategoryUrl(
                categories[0].type,
                categoryUrlProductType
            );
        }
        return resolveProductTypeKeyForCategoryUrl(undefined, categoryUrlProductType);
    }, [categories, categoryUrlProductType]);
    
    const specialUrl = useMemo(() => {
        if (!pageUrls || !resolvedLanguageCode) {
            return `/${productTypeForSpecial}/special`;
        }
        return buildSpecialUrl(
            productTypeForSpecial,
            'special',
            pageUrls,
            resolvedLanguageCode,
            defaultLanguage
        );
    }, [productTypeForSpecial, pageUrls, resolvedLanguageCode, defaultLanguage]);
    
    const normalizeCategoryUrl = useCallback((category: CategoryData, defLang: string = 'en'): string => {
        const productType = resolveProductTypeKeyForCategoryUrl(
            category.type,
            categoryUrlProductType
        );
        const slug = category.slug;
        
        if (slug) {
            if (pageUrls) {
                return buildCategoryUrl(productType, slug, pageUrls, resolvedLanguageCode || defLang, defaultLanguage);
            }
        }
        
        if (pageUrls) {
            const categoryTemplateKey = `${productType}-cat`;
            const baseUrl = resolveUrlFromMap(
                pageUrls,
                categoryTemplateKey,
                resolvedLanguageCode || defLang,
                defaultLanguage,
                `/${productType}`
            );
            return `${baseUrl}/category/${category.id}`;
        }
        
        return `/${productType}/${slug || `category/${category.id}`}`;
    }, [pageUrls, resolvedLanguageCode, defaultLanguage, categoryUrlProductType]);

    const Cats = useMemo(() => {
        if (!Array.isArray(categories)) {
            return [];
        }
        return categories.map((category) => {
            const href = normalizeCategoryUrl(category, resolvedLanguageCode || 'en');
            return {
                title: category.name,
                href: href,
            };
        });
    }, [categories, normalizeCategoryUrl, resolvedLanguageCode]);

    if (!loading && categories.length === 0) {
        return (
            <section className={`${SPECIAL_TOUR_SECTION_SHELL} ${SPECIAL_TOUR_SECTION_MY}`}>
                <div className="text-center text-alsero-light-gray border-2 border-dashed border-gray-200 rounded-2xl p-8">
                    {!categoryIds || categoryIds.length === 0 
                        ? getVariable('categories_no_selection', 'Bu component için kategori seçimi yapılmamış. Lütfen sayfa editöründe component\'i seçin ve "Kategorileri Seç" butonunu kullanın.')
                        : getVariable('categories_not_found', 'Seçilen kategoriler bulunamadı.')
                    }
                </div>
            </section>
        );
    }

    if (loading) {
        return <SpecialTourGridSkeleton />;
    }

    if (error) {
        return (
            <section className={`${SPECIAL_TOUR_SECTION_SHELL} ${SPECIAL_TOUR_SECTION_MY}`}>
                <div className="text-center text-red-500">
                    {getVariable('categories_load_error', 'Hata:')} {error}
                </div>
            </section>
        );
    }
    
    const buildHref = (href: string) => `${languagePrefix}${href}`;

    return (
        <motion.section
            key="panel-1"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{
                duration: 0.15,
                ease: "easeOut"
            }}
            className={`${SPECIAL_TOUR_SECTION_SHELL} ${SPECIAL_TOUR_SECTION_MY}`}
        >
            <div className="flex flex-row justify-between">
                <div ref={titleRef} className="flex flex-col gap-1">
                    <h2
                        data-prop-path="title"
                        className="font-semibold text-alsero-second text-2xl sm:text-3xl md:text-4xl leading-tight"
                    >
                        {title}
                    </h2>
                    <motion.div 
                        className="h-[6px] bg-alsero-first"
                        initial={{ width: 0 }}
                        animate={{ width: isTitleInView ? '100px' : 0 }}
                        transition={{
                            duration: 0.3,
                            ease: "easeOut",
                            delay: 0.2
                        }}
                    ></motion.div>
                </div>
                <Link
                    href={buildHref(specialUrl)}
                    className="font-medium text-lg bg-alsero-first rounded-sm text-center px-6 py-3.5 text-white hover:text-white hover:bg-alsero-light-gray transition-all duration-300 ease-in-out"
                >
                    View All
                </Link>
            </div>
            <motion.div 
                className="mt-8 grid grid-cols-1 gap-6 sm:mt-12 md:grid-cols-2 md:gap-8 lg:grid-cols-3"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "0px" }}
                variants={{
                    hidden: { opacity: 0 },
                    visible: {
                        opacity: 1,
                        transition: {
                            staggerChildren: 0.1,
                            delayChildren: 0.1
                        }
                    }
                }}
            >
                {Cats.map((cat, index) => (
                    <motion.div
                        key={cat.href}
                        variants={{
                            hidden: { opacity: 0, y: 20 },
                            visible: { opacity: 1, y: 0 }
                        }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        whileHover={{ y: -5 }}
                        className={`group ${index === 6 && Cats.length > 6 ? 'md:col-start-2 lg:col-start-2' : ''}`}
                    >
                        <Link 
                            href={buildHref(cat.href)} 
                            className="flex items-center justify-center w-full h-32 rounded-lg bg-alsero-dark-gray hover:bg-alsero-light-gray transition-colors duration-300 px-8 ease-in-out"
                        >
                            <h3 className="text-[22px] font-bold text-white text-center font-sans tracking-[0.25em] leading-tight">
                                {cat.title}
                            </h3>
                        </Link>
                    </motion.div>
                ))}
            </motion.div>
        </motion.section>
    )
}
